import React from 'react'
import styled from '@emotion/styled'
import {MealPlan} from '@findhotel/sapi'
import {Spacing} from 'types/Theme'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {cssTheme} from '@vio/atlas/themes'
import {
  type Card,
  PaymentMethodType
} from '@vio/core/src/api-types/bovio/response/payment_methods_settings'
import {defaultCurrencyCode} from '@vio/core/src/localization/business/currency'
import {
  getRefundType,
  RefundType
} from '@vio/core/src/offer/business/cancellationPenalties'
import {OfferPaymentMode} from '@vio/core/src/offer/business/offerPaymentMode'
import {CancellationPenalty} from '@vio/core/src/offer/types/offer'
import {
  getDisplayPrice,
  getPriceCurrencyCode,
  getTotalUpFrontPrice,
  Price
} from '@vio/core/src/price/business/price'

import {CancellationPenalties} from '../../booking/CancellationPenalties'
import {DealDetailsMeal} from '../../booking/DealDetails/DealDetailsMeal'
import {PaymentMethods} from '../../booking/DealDetails/PaymentMethods'
import {DividerComponent, SpacingWrapper} from './roomDetailsOverlayHelpers'

export interface RoomDealDetailsProps {
  cancellationPenalties: CancellationPenalty[]
  prices: Price[]
  hotelTimeZone?: string
  mealPlan: MealPlan[]
  paymentMethodType?: OfferPaymentMode
  supportedCards?: Card[]
  availablePaymentMethods?: PaymentMethodType[]
}

interface DetailSection {
  key: string
  content: React.ReactNode
  paddingBottom?: Spacing
}

const SectionCard = styled.div`
  border: 1px solid ${cssTheme.colors.border.neutral.secondary};
  border-radius: ${cssTheme.layout.radius.lg};
  overflow: hidden;
`

const CardSection = ({
  children,
  paddingBottom = 's600'
}: {
  children: React.ReactNode
  paddingBottom?: Spacing
}) => (
  <Box
    paddingLeft="s500"
    paddingRight="s500"
    paddingTop="s600"
    paddingBottom={paddingBottom}
  >
    {children}
  </Box>
)

const Section = ({
  children,
  isMobile,
  paddingBottom
}: {
  children: React.ReactNode
  isMobile: boolean
  paddingBottom?: Spacing
}) =>
  isMobile ? (
    <SpacingWrapper isMobile>{children}</SpacingWrapper>
  ) : (
    <CardSection paddingBottom={paddingBottom}>{children}</CardSection>
  )

const SectionDivider = ({isMobile}: {isMobile: boolean}) =>
  isMobile ? <DividerComponent isMobile /> : <Divider />

export const RoomDealDetails = ({
  cancellationPenalties,
  prices,
  hotelTimeZone,
  mealPlan,
  paymentMethodType,
  supportedCards = [],
  availablePaymentMethods = []
}: RoomDealDetailsProps) => {
  const {isMobile} = useDeviceLayout()

  const displayPrice = getDisplayPrice(prices)
  // Hotel fees are paid at the property and never refunded, so they stay out
  // of the refund total, matching CP and BMP.
  const totalPrice = getTotalUpFrontPrice(displayPrice) ?? 0
  const currencyCode = getPriceCurrencyCode(displayPrice) || defaultCurrencyCode

  const sections: DetailSection[] = [
    {
      key: 'cancellation',
      content: (
        <CancellationPenalties
          cancellationPenalties={cancellationPenalties}
          totalPrice={totalPrice}
          displayCurrencyCode={currencyCode}
          hotelTimeZone={hotelTimeZone}
        />
      ),
      paddingBottom:
        getRefundType(cancellationPenalties) === RefundType.nonRefundable
          ? 's000'
          : 's300'
    },
    ...(mealPlan.length > 0
      ? [
          {
            key: 'meal-plan',
            content: <DealDetailsMeal mealPlan={mealPlan} />
          }
        ]
      : []),
    ...(paymentMethodType !== undefined
      ? [
          {
            key: 'payment-methods',
            content: (
              <PaymentMethods
                paymentMethodType={paymentMethodType}
                cancellationPenalties={cancellationPenalties}
                supportedCards={supportedCards}
                availablePaymentMethods={availablePaymentMethods}
              />
            )
          }
        ]
      : [])
  ]

  const content = sections.map((section, index) => (
    <React.Fragment key={section.key}>
      {index > 0 && <SectionDivider isMobile={isMobile} />}
      <Section isMobile={isMobile} paddingBottom={section.paddingBottom}>
        {section.content}
      </Section>
    </React.Fragment>
  ))

  if (isMobile) {
    return (
      <>
        {content}
        {paymentMethodType !== undefined && <DividerComponent isMobile />}
      </>
    )
  }

  return (
    <Box
      paddingLeft="s600"
      paddingRight="s600"
      paddingTop="s700"
      paddingBottom="s700"
    >
      <SectionCard>{content}</SectionCard>
    </Box>
  )
}
